import { HttpStatusCode } from '@angular/common/http';
import { computed, effect, Injectable, resource } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@env';
import { filter } from 'rxjs';
import { User, USER_ROLE } from './user.model';
import { getHeaders } from './header';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly DEFAULT_ANON_USER = new User('Anonymous', '', USER_ROLE.ANON);

  private readonly _userResource = resource({
    loader: async ({ abortSignal }) => {
      const headers = getHeaders();

      const res = await fetch(environment.privateApiPrefix + environment.me, {
        redirect: 'manual',
        headers,
        signal: abortSignal,
      });
      if ([HttpStatusCode.TemporaryRedirect, HttpStatusCode.Unauthorized].includes(res.status) || !res.ok) {
        environment.apiPrefix = environment.publicApiPrefix;
        return this.DEFAULT_ANON_USER;
      } else {
        environment.apiPrefix = environment.privateApiPrefix;
        return res.json().then((user) => new User(user.name, user.email, user.role));
      }
    },
  });

  user = computed(() => {
    if (this._userResource.error()) return this.DEFAULT_ANON_USER;
    return this._userResource.value(); // Returns undefined while loading
  });
  user$ = toObservable(this.user).pipe(filter((u) => !!u)); // Wait for used to be defined

  isLoading = computed(() => this._userResource.isLoading());
  
  isAnonymous = computed(() => this.user()?.isAnonymous() ?? false);
  isBuyer = computed(() => this.user()?.isBuyer ?? false);
  isSeller = computed(() => this.user()?.isSeller() ?? false);
  isAdmin = computed(() => this.user()?.isAdmin() ?? false);

  constructor() {
    effect(() => {
      const currentUser = this.user();
      if (currentUser) {
        this.defineApiPrefix(currentUser.role);
      }
    });
  }

  refreshUser() {
    this._userResource.reload();
  }

  private defineApiPrefix(userRole: USER_ROLE) {
    if (userRole === USER_ROLE.ANON) {
      environment.apiPrefix = environment.publicApiPrefix;
    } else {
      environment.apiPrefix = environment.privateApiPrefix;
    }
  }
}
